import * as React from "react";
import { useMemo, useState } from "react";
import { Bell, BellOff, RefreshCw, AlertCircle, Mail, ChevronRight } from "lucide-react";
import { toast } from "sonner";
import { Link } from "react-router-dom";

import { Button, Switch, Spinner } from "@/components/ui";
import { NotificationSubscriptionServices } from "@/services/bedrock/NotificationSubscriptionServices";
import { NotificationSubscriptionOption, NotificationSubscriptionParam } from "@/services/bedrock/dtos";
import { cn } from "@/lib/utils";
import { useAccountSettings, URLS } from "@/utils";
import { WebComponents } from "@/interfaces";

interface NotificationSubscriptionsSectionProps {
  userId: string | number;
  isDisabled: boolean;
  isOwnProfile?: boolean;
}

export const NotificationSubscriptionsSection: React.FC<NotificationSubscriptionsSectionProps> = ({
  userId,
  isDisabled,
  isOwnProfile = false,
}) => {
  const [subscriptions, setSubscriptions] = useState<NotificationSubscriptionOption[]>([]);
  const [originalSubscriptions, setOriginalSubscriptions] = useState<NotificationSubscriptionOption[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [saving, setSaving] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);

  const { hasReadPermission } = useAccountSettings();
  const hasDigestPermission = hasReadPermission(WebComponents.DIGEST_EMAIL);

  const fetchSubscriptions = async () => {
    setLoading(true);
    setError(null);
    try {
      const data = await NotificationSubscriptionServices.getNotificationSubscriptions(userId);
      setSubscriptions(data || []);
      setOriginalSubscriptions(JSON.parse(JSON.stringify(data || [])));
    } catch (err: any) {
      console.error("Error fetching notification subscriptions:", err);
      setError(err?.message || "Failed to load notification subscriptions.");
    } finally {
      setLoading(false);
    }
  };

  React.useEffect(() => {
    fetchSubscriptions();
  }, [userId]);

  const isDirty = useMemo(() => {
    if (!subscriptions || !originalSubscriptions) return false;
    if (subscriptions.length !== originalSubscriptions.length) return true;
    return subscriptions.some((sub, index) => {
      const orig = originalSubscriptions[index];
      return sub.eventType !== orig.eventType || sub.subscribed !== orig.subscribed;
    });
  }, [subscriptions, originalSubscriptions]);

  const handleToggle = (eventType: string, checked: boolean) => {
    if (isDisabled || saving) return;
    setSubscriptions(prev => prev.map(sub => (sub.eventType === eventType ? { ...sub, subscribed: checked } : sub)));
  };

  const handleSave = async () => {
    if (isDisabled || saving) return;
    setSaving(true);
    try {
      const payload: NotificationSubscriptionParam[] = subscriptions.map(sub => ({
        eventType: sub.eventType,
        subscribed: sub.subscribed,
      }));
      const updated = await NotificationSubscriptionServices.updateNotificationSubscriptions(userId, payload);
      setSubscriptions(updated || []);
      setOriginalSubscriptions(JSON.parse(JSON.stringify(updated || [])));
      toast.success("Notification preferences saved successfully");
    } catch (err: any) {
      console.error("Error saving notification subscriptions:", err);
      toast.error(err?.message || "Failed to save notification preferences");
    } finally {
      setSaving(false);
    }
  };

  const handleDiscard = () => {
    setSubscriptions(JSON.parse(JSON.stringify(originalSubscriptions)));
  };

  return (
    <div className="flex flex-col gap-6">
      <div className="flex flex-col gap-1 border-b border-slate-100 pb-3">
        <h4 className="text-xl font-bold text-slate-800 flex items-center gap-2">
          <Bell className="size-5 text-primary" />
          <span>Notification Preferences</span>
        </h4>
        <p className="text-sm text-slate-500 leading-relaxed">
          {isOwnProfile ? (
            <>
              <strong>What notifications do you receive?</strong> Manage the notifications and automated alerts you will
              receive.
            </>
          ) : (
            <>
              <strong>What notifications do they receive?</strong> Manage the notifications and automated alerts this
              assistant will receive.
            </>
          )}
        </p>
      </div>

      {/* UX Banner for Recurring Digests */}
      {!loading && !error && hasDigestPermission && (
        <div className="bg-blue-50/40 border border-blue-100/60 rounded-2xl p-4 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4 transition-all hover:bg-blue-50/60">
          <div className="flex gap-3.5 items-start">
            <div className="size-9 rounded-full bg-blue-100/50 flex items-center justify-center text-blue-600 shrink-0">
              <Mail className="size-5" />
            </div>
            <div className="flex flex-col gap-1">
              <span className="text-sm font-semibold text-slate-800">Looking for the recurring digest settings?</span>
              <p className="text-xs text-slate-500 leading-relaxed max-w-xl">
                Weekly summaries, daily action updates, and monthly strategic performance reports can be scheduled for
                delivery via the dedicated Digest Email center.
              </p>
            </div>
          </div>
          <Link
            to={URLS.coach.digestEmails}
            className="text-xs font-bold text-primary hover:text-blue-800 transition-colors focus:outline-none cursor-pointer flex items-center gap-1 shrink-0 bg-blue-100/30 hover:bg-blue-100/60 px-3.5 py-1.5 rounded-lg border border-blue-200/30 self-stretch sm:self-auto justify-center"
          >
            <span>Manage Digests</span>
            <ChevronRight className="size-3.5" />
          </Link>
        </div>
      )}

      {loading ? (
        <div className="flex flex-col items-center justify-center py-12 gap-3">
          <Spinner className="size-8 text-primary" />
          <span className="text-xs text-slate-400 font-medium">Loading preferences...</span>
        </div>
      ) : error ? (
        <div className="bg-destructive/10 border border-destructive/20 rounded-xl p-4 flex flex-col sm:flex-row items-center justify-between gap-4">
          <div className="flex items-center gap-3">
            <AlertCircle className="size-5 text-destructive shrink-0" />
            <span className="text-xs font-semibold text-destructive">{error}</span>
          </div>
          <Button
            type="button"
            variant="outline"
            size="sm"
            onClick={fetchSubscriptions}
            className="flex items-center gap-1.5"
          >
            <RefreshCw className="size-3.5" />
            <span>Retry</span>
          </Button>
        </div>
      ) : subscriptions.length === 0 ? (
        <div className="flex flex-col items-center justify-center py-10 gap-2 border border-dashed border-slate-200 rounded-xl">
          <BellOff className="size-8 text-slate-300" />
          <p className="text-xs text-slate-400 font-medium">No notification options available.</p>
        </div>
      ) : (
        <div className="flex flex-col gap-4">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {subscriptions.map(sub => (
              <div
                key={sub.eventType}
                className={cn(
                  "border rounded-xl p-4 flex items-start justify-between gap-4 transition-all bg-slate-50/20",
                  sub.subscribed
                    ? "border-primary/20 bg-blue-50/5 ring-1 ring-blue-500/5"
                    : "border-slate-100 hover:border-slate-200"
                )}
              >
                <div className="flex flex-col gap-1">
                  <span className="font-bold text-sm text-slate-800">{sub.name}</span>
                  <p className="text-xs text-slate-500 leading-relaxed">{sub.description}</p>
                </div>
                <Switch
                  checked={sub.subscribed}
                  onCheckedChange={checked => handleToggle(sub.eventType, checked)}
                  disabled={isDisabled || saving}
                />
              </div>
            ))}
          </div>

          {/* Action Row */}
          {isDirty && (
            <div className="flex items-center justify-end gap-3 pt-4 border-t border-slate-100 mt-2">
              <Button type="button" variant="outline" size="sm" onClick={handleDiscard} disabled={saving || isDisabled}>
                Discard
              </Button>
              <Button
                type="button"
                variant="default"
                size="sm"
                onClick={handleSave}
                loading={saving}
                disabled={isDisabled}
              >
                Save Preferences
              </Button>
            </div>
          )}
        </div>
      )}
    </div>
  );
};
